import type { ReactNode } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

// État de la modale
interface ModalState {
  children: ReactNode | null;
  closable: boolean;
  onClose?: () => void;
  onValidate?: () => void;
}

interface UIState {
  // État de la modale
  modal: ModalState;
  openModal: (
    modal: Omit<ModalState, "children"> & { children: ReactNode }
  ) => void;
  closeModal: (validated?: boolean) => void;

  // État de la barre latérale
  displaySidenav: boolean;
  toggleSidenav: () => void;
  setSidenav: (visible: boolean) => void;

  // Position de défilement
  scrollYPosition: number;
  setScrollYPosition: (position: number) => void;

  // Préférence de tri des mails
  isMailsAreSortedByColors: boolean;
  toggleMailsSortByColors: () => void;

  // Visibilité des étapes d'aide
  isHelpStepsShown: boolean;
  setHelpStepsShown: (shown: boolean) => void;

  // Modale de tutoriel
  isTutorialOpen: boolean;
  openTutorial: () => void;
  closeTutorial: () => void;

  // Date du jour (base des calculs de dates relatives)
  todayDate: Date;
  setTodayDate: (date: Date) => void;

  // État de chargement
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;

  // Filtre des mails
  mailFilter: "all" | "important" | "sent" | "drafts";
  setMailFilter: (filter: "all" | "important" | "sent" | "drafts") => void;

  // Requête de recherche
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Modale
      modal: { children: null, closable: false },
      openModal: (modal) => set({ modal }),
      closeModal: (validated = false) => {
        const { modal } = get();
        if (!validated && modal.onClose) modal.onClose();
        if (validated && modal.onValidate) modal.onValidate();
        set({ modal: { children: null, closable: false } });
      },

      // Barre latérale
      displaySidenav: true,
      toggleSidenav: () =>
        set((state) => ({ displaySidenav: !state.displaySidenav })),
      setSidenav: (visible) => set({ displaySidenav: visible }),

      // Position de défilement
      scrollYPosition: 0,
      setScrollYPosition: (position) => set({ scrollYPosition: position }),
      // Tri des mails
      isMailsAreSortedByColors: false,
      toggleMailsSortByColors: () =>
        set((state) => ({
          isMailsAreSortedByColors: !state.isMailsAreSortedByColors,
        })),

      // Étapes d'aide
      isHelpStepsShown: false,
      setHelpStepsShown: (shown) => set({ isHelpStepsShown: shown }),

      // Modale de tutoriel
      isTutorialOpen: false,
      openTutorial: () => set({ isTutorialOpen: true }),
      closeTutorial: () => set({ isTutorialOpen: false }),

      // Date du jour
      todayDate: new Date(),
      setTodayDate: (date) => set({ todayDate: date }),

      // Chargement
      isLoading: false,
      setIsLoading: (loading) => set({ isLoading: loading }),

      // Filtre des mails
      mailFilter: "all",
      setMailFilter: (filter) => set({ mailFilter: filter }),

      // Requête de recherche
      searchQuery: "",
      setSearchQuery: (query) => set({ searchQuery: query }),
    }),
    {
      name: "inbasket-ui-storage",
      storage: createJSONStorage(() => sessionStorage),
      // Exclut de la persistance l'état des modales et l'état d'exécution
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(
            ([key]) =>
              !["modal", "isLoading", "searchQuery", "isTutorialOpen"].includes(
                key
              )
          )
        ) as UIState,
    }
  )
);

export default useUIStore;
